import * as XLSX from 'xlsx'

export interface ParsedPilotRow {
  participantNumber: string
  srsId: string
  tracker: string
  trackserial: string
  firstName: string
  lastName: string
  confirmationStatus: string
  registrationTime: string
  email: string
  dateOfBirth: string
  gender: string
  foodChoice: string
  faiNation: string
  address: string
  city: string
  postalCode: string
  administrativeRegion: string
  countryOfResidence: string
  mobileTelNumber: string
  whatsappNumber: string
  accommodationDuringEvent: string
  harnessManufacturer: string
  harnessModel: string
  paragliderManufacturer: string
  paragliderModel: string
  paragliderColor: string
  paragliderCertification: string
  gliderSize: string
  srsRanking: string
  maximumRatedRangeKg: string
  sponsor: string
  faiLicenseNumber: string
  civlId: string
  rankingAtReqDate: string
  latestRanking: string
  nationalFederationLicenseId: string
  club: string
  team: string
  tshirtSize: string
  insuranceCompany: string
  insurancePolicyNumber: string
  insurancePhoneNumber: string
  nameInNationalAlphabet: string
  emergencyContactName: string
  emergencyContactPhone: string
  bloodType: string
}

export interface XlsxParseResult {
  rows: ParsedPilotRow[]
  skippedRows: number
  totalDataRows: number
  errors: string[]
}

const COLUMN_MAP: Record<string, keyof ParsedPilotRow> = {
  'Participant number': 'participantNumber',
  'Srs id': 'srsId',
  'Tracker': 'tracker',
  'Trackserial': 'trackserial',
  'Name': 'firstName',
  'Surname': 'lastName',
  'Confirmation status': 'confirmationStatus',
  'Registration time': 'registrationTime',
  'Email': 'email',
  'Date of birth': 'dateOfBirth',
  'Gender': 'gender',
  'Food choice': 'foodChoice',
  'Fai nation (sporting licence)': 'faiNation',
  'Address': 'address',
  'City': 'city',
  'Postal code': 'postalCode',
  'Administrative region (state, province...)': 'administrativeRegion',
  'Country of residence': 'countryOfResidence',
  'Mobile tel number': 'mobileTelNumber',
  'Whatsapp number': 'whatsappNumber',
  'Accommodation during the event': 'accommodationDuringEvent',
  'Harness manufacturer': 'harnessManufacturer',
  'Harness model': 'harnessModel',
  'Paraglider manufacturer': 'paragliderManufacturer',
  'Paraglider model': 'paragliderModel',
  'Paraglider color': 'paragliderColor',
  'Paraglider certification': 'paragliderCertification',
  'Glider size': 'gliderSize',
  'Srs ranking': 'srsRanking',
  'Maximum rated range in kg': 'maximumRatedRangeKg',
  'Sponsor': 'sponsor',
  'Fai license number': 'faiLicenseNumber',
  'Civl id': 'civlId',
  'Ranking at req date': 'rankingAtReqDate',
  'Latest ranking': 'latestRanking',
  'National federation license id': 'nationalFederationLicenseId',
  'Club': 'club',
  'Team': 'team',
  'T-shirt size': 'tshirtSize',
  'Insurance company': 'insuranceCompany',
  'Insurance policy number': 'insurancePolicyNumber',
  'Insurance phone number': 'insurancePhoneNumber',
  'Your name in national alphabet': 'nameInNationalAlphabet',
  'Your emergency contact': 'emergencyContactName',
  'Their phone number': 'emergencyContactPhone',
  'Blood type': 'bloodType',
}

const EXPECTED_HEADERS = Object.keys(COLUMN_MAP)
const VALID_STATUSES = new Set(['Confirmed', 'Team pilot'])

/**
 * Convert an Excel serial date number to a DD.MM.YYYY string.
 * Excel serial dates count days since 1899-12-30 (with the Lotus 1-2-3 leap year bug).
 */
function excelSerialToDate(serial: number): string {
  const epoch = new Date(1899, 11, 30) // Dec 30, 1899
  const date = new Date(epoch.getTime() + serial * 86400000)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

export function parseXlsxBuffer(buffer: ArrayBuffer): XlsxParseResult {
  let workbook: XLSX.WorkBook
  try {
    workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' })
  } catch {
    return { rows: [], skippedRows: 0, totalDataRows: 0, errors: ['File is not a valid XLSX file'] }
  }

  const sheetName = workbook.SheetNames[0]
  if (!sheetName) {
    return { rows: [], skippedRows: 0, totalDataRows: 0, errors: ['File is not a valid XLSX file'] }
  }

  const sheet = workbook.Sheets[sheetName]
  const rawRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })

  if (rawRows.length === 0) {
    return { rows: [], skippedRows: 0, totalDataRows: 0, errors: ['XLSX file contains no pilot data'] }
  }

  const headers = Object.keys(rawRows[0])
  const headerSet = new Set(headers)
  const expectedSet = new Set(EXPECTED_HEADERS)
  const missing = EXPECTED_HEADERS.filter(h => !headerSet.has(h))
  const unrecognised = headers.filter(h => !expectedSet.has(h))

  if (missing.length > 0 || unrecognised.length > 0) {
    const parts: string[] = []
    if (missing.length > 0) parts.push(`Missing: ${missing.join(', ')}`)
    if (unrecognised.length > 0) parts.push(`Unrecognised: ${unrecognised.join(', ')}`)
    return { rows: [], skippedRows: 0, totalDataRows: rawRows.length, errors: [`Invalid column headers. ${parts.join('. ')}`] }
  }

  const totalDataRows = rawRows.length
  const rows: ParsedPilotRow[] = []
  let skippedRows = 0

  for (const rawRow of rawRows) {
    const status = String(rawRow['Confirmation status'] ?? '')
    if (!VALID_STATUSES.has(status)) { skippedRows++; continue }
    const row = {} as ParsedPilotRow
    for (const [header, field] of Object.entries(COLUMN_MAP)) {
      ;(row as unknown as Record<string, string>)[field] = String(rawRow[header] ?? '')
    }
    // Convert Excel serial date numbers to DD.MM.YYYY format
    if (row.dateOfBirth && /^\d+$/.test(row.dateOfBirth)) {
      const serial = parseInt(row.dateOfBirth, 10)
      if (serial > 1000 && serial < 100000) {
        row.dateOfBirth = excelSerialToDate(serial)
      }
    }
    rows.push(row)
  }

  return { rows, skippedRows, totalDataRows, errors: [] }
}
